import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Upload, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline microphone recorder + file upload for lesson voice notes.
 * Calls `onSubmit(blob, filename)` with the audio payload.
 */
export default function VoiceRecorder({ onSubmit, busy, disabled }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [filename, setFilename] = useState("recording.webm");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    if (mediaRef.current && mediaRef.current.state === "recording") {
      mediaRef.current.stop();
    }
  }, [blobUrl]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setBlob(b);
        setFilename(`recording-${Date.now()}.${(mr.mimeType || "audio/webm").includes("webm") ? "webm" : "mp4"}`);
        setBlobUrl(URL.createObjectURL(b));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    if (mediaRef.current?.state === "recording") mediaRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Audio must be ≤ 25 MB"); return; }
    setBlob(f);
    setFilename(f.name);
    setBlobUrl(URL.createObjectURL(f));
  };

  const reset = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null); setBlobUrl(null); setElapsed(0);
  };

  const fmtElapsed = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const r = (s % 60).toString().padStart(2, "0");
    return `${m}:${r}`;
  };

  return (
    <div className="border border-white/10 p-4 bg-white/[0.02]" data-testid="voice-recorder">
      <div className="flex flex-wrap items-center gap-3">
        {!recording && !blob && (
          <>
            <button
              onClick={start} disabled={disabled || busy}
              className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
              data-testid="voice-record-start"
            >
              <Mic size={14} /> Record
            </button>
            <label className="btn-secondary px-4 py-2 text-xs inline-flex items-center gap-2 cursor-pointer" data-testid="voice-file-upload-label">
              <Upload size={14} /> Upload audio
              <input type="file" accept="audio/*" onChange={onFile} className="hidden" data-testid="voice-file-input" />
            </label>
            <div className="text-xs text-[#A3A3A3]">mp3 · wav · m4a · webm · ≤ 25 MB</div>
          </>
        )}
        {recording && (
          <>
            <span className="inline-flex items-center gap-2 text-xs font-mono">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              {fmtElapsed(elapsed)}
            </span>
            <button
              onClick={stop}
              className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
              data-testid="voice-record-stop"
            >
              <Square size={14} /> Stop
            </button>
          </>
        )}
        {blob && !recording && (
          <>
            <audio src={blobUrl} controls className="h-8" data-testid="voice-preview" />
            <button
              onClick={() => onSubmit(blob, filename)}
              disabled={busy}
              className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2"
              data-testid="voice-transcribe-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
              {busy ? "Transcribing…" : "Transcribe & save"}
            </button>
            <button
              onClick={reset}
              disabled={busy}
              className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2"
              data-testid="voice-reset"
            >
              <Trash2 size={14} /> Discard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
